/**
 * Admin "act as rep" state.
 *
 * When an admin views a rep's field surface (Today's Plan / My
 * Territories), the selected rep is stashed here. The API client reads
 * it and attaches `X-Act-As-Rep: <userId>` to every `/api/field/*`
 * request, so the backend scopes reads + writes to that rep (see the
 * `get_field_actor` dependency in hooten-young-sales).
 *
 * Backed by `sessionStorage` so it survives in-app navigation (e.g.
 * drilling into an account) and a page reload, but never leaks past the
 * tab/session. Non-admins can't do anything with it — the backend
 * rejects the header unless the caller is an admin.
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'hy.field.actAsRep';
const CHANGE_EVENT = 'hy:act-as-changed';

export interface ActAsRep {
  id: string;
  name: string;
}

/** Internal reader. Callers outside this module use `useActAsRep`
 *  (reactive) or `getActAsRepId` (for the request header). */
function getActAsRep(): ActAsRep | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActAsRep) : null;
  } catch {
    return null;
  }
}

/**
 * Is this path part of the rep workspace?
 *
 * The single definition of where "act as rep" applies. Both the API
 * client (which attaches the header) and ActingAsBanner (which tells
 * the admin they're impersonating) read this, so impersonation can
 * never be in effect on a page that doesn't say so.
 *
 * That pairing matters for /api/notes/*: the Team Chat tab lives inside
 * the rep workspace, but the standalone /notes page is the admin's own
 * mail. Scoping by path keeps the rep's private messages inside the
 * clearly-labelled view.
 */
export function isRepSurfacePath(pathname: string): boolean {
  // Deliberately NOT the bare "/regional-sales". That route is the
  // signed-in user's OWN workspace; the impersonated one lives at
  // /admin/regional-sales/reps/:id/view. Matching the prefix meant
  // that merely opening your own Regional Sales card re-activated a
  // rep view left in sessionStorage from an earlier session — the page
  // announced "Acting as George Cost" when nobody had asked to.
  //
  // Account detail stays impersonatable: it is shared by both
  // workspaces, and drilling into an account from a rep view has to
  // keep showing that rep's data.
  return (
    pathname.startsWith('/admin/regional-sales/reps/') ||
    pathname.startsWith('/regional-sales/accounts/')
  );
}

/** The rep id the client should impersonate, or null. */
export function getActAsRepId(): string | null {
  return getActAsRep()?.id ?? null;
}

/** Set (or clear, with null) the impersonated rep and notify subscribers. */
/**
 * Every cache prefix that can hold impersonated data.
 *
 * `field` is obvious. `notes` is the one that got missed: the Team
 * Chat tab inside a rep workspace fetches with the act-as header, but
 * its query keys carry no identity — so a rep's private conversations
 * sat in the same cache slot the admin's own /notes page reads from.
 */
const IMPERSONATED_CACHE_PREFIXES = [['field'], ['notes']] as const;

function purgeImpersonatedCache(queryClient: QueryClient): void {
  for (const queryKey of IMPERSONATED_CACHE_PREFIXES) {
    queryClient.removeQueries({ queryKey });
  }
}

/**
 * Start acting as a rep, discarding whatever the cache holds first.
 *
 * Paired with `endRepView` so switching identity and clearing the
 * previous one's data cannot come apart. They were separate calls
 * before, and the four places that changed identity purged different
 * things — one purged nothing at all.
 */
export function beginRepView(queryClient: QueryClient, rep: ActAsRep): void {
  purgeImpersonatedCache(queryClient);
  setActAsRep(rep);
}

/**
 * Update the displayed name for the rep already being acted as.
 *
 * Separate from `beginRepView` because the identity isn't changing —
 * purging the cache here would discard the rep's data at the exact
 * moment their profile resolved and it started arriving.
 */
export function renameRepView(rep: ActAsRep): void {
  if (getActAsRep()?.id !== rep.id) return;
  setActAsRep(rep);
}

/** Stop acting as anyone, and drop that rep's cached data with them. */
export function endRepView(queryClient: QueryClient): void {
  setActAsRep(null);
  purgeImpersonatedCache(queryClient);
}

/** Internal. Callers use `beginRepView` / `endRepView`, which cannot
 *  leave a previous identity's data behind. */
function setActAsRep(rep: ActAsRep | null): void {
  if (rep) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rep));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * Ends any rep view, and drops that rep's cached data with it.
 *
 * Impersonation is stored in sessionStorage so it survives a reload
 * and in-app navigation — which it must, because drilling into an
 * account moves between routes. The cost is that it also survives
 * *leaving*: an admin who viewed a rep and then went home found the
 * next click on "Regional Sales" silently resumed acting as that
 * person, days later, with no intent to.
 *
 * Being on your own home page is the unambiguous signal that you are
 * yourself again, so that is where the session ends. "Exit rep view"
 * still exists for leaving deliberately from inside.
 */
export function useEndRepViewOnHome(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (getActAsRep() === null) return;
    endRepView(queryClient);
  }, [queryClient]);
}

/** Reactive hook — re-renders when the impersonated rep changes. */
export function useActAsRep(): ActAsRep | null {
  const [rep, setRep] = useState<ActAsRep | null>(getActAsRep);
  useEffect(() => {
    const handler = () => setRep(getActAsRep());
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);
  return rep;
}
